import { RotateCcw, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import Currency from "components/format/currency/currency";
import { btnIconCls, errorTextCls } from "components/tw/classes";
import type { InvoiceableOrderLine } from "services/invoices.service";

/**
 * A line as the user currently has it: the order's values plus any edits.
 * Quantity and price hold what was TYPED, so a field the user cleared stays
 * empty rather than snapping to 0 — and an empty or zero quantity can be shown
 * as the mistake it is instead of being invoiced.
 */
export type EditedLine = Omit<InvoiceableOrderLine, "quantity" | "price"> & {
  quantity: number | string;
  price: number | string;
  removed?: boolean;
};

/**
 * What is wrong with a kept line, or null. A quantity must be above zero — a
 * 0-quantity line on a built invoice is a legal document billing nothing — and
 * a price must be a number of zero or more (a free item is legitimate).
 */
export function lineProblem(line: EditedLine): "quantity" | "price" | null {
  if (line.removed) return null;
  const quantity = line.quantity === "" ? NaN : Number(line.quantity);
  if (!Number.isFinite(quantity) || quantity <= 0) return "quantity";
  const price = line.price === "" ? NaN : Number(line.price);
  if (!Number.isFinite(price) || price < 0) return "price";
  return null;
}

type Props = {
  lines: EditedLine[];
  disabled?: boolean;
  onChange: (externalCode: string, patch: Partial<EditedLine>) => void;
};

// 16 px on a phone: iOS Safari zooms into any smaller field that gets focus.
const cellInputCls =
  "w-24 max-md:w-full rounded-md border border-zinc-300 bg-transparent px-2 py-1 text-right text-base md:text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 disabled:opacity-60 aria-[invalid=true]:border-red-500 dark:border-zinc-700";

// Labels the fields of a phone card, where the table's header row is hidden.
const phoneLabelCls = "mb-1 block text-xs text-zinc-500 md:hidden";

/**
 * The products an order will be invoiced for, editable in place.
 *
 * A table from `md`; on a phone each line is a card (name on top, quantity and
 * price side by side, then the total and the remove button) because five
 * columns do not fit and the remove button ended up past the dialog's edge.
 * Same markup both ways: the table elements just stop being table cells.
 *
 * Quantity and price can be changed and a line can be dropped before anything
 * is issued — the same edits the single-invoice form allows before saving,
 * which is the point: with "Build invoices" there is no draft to correct
 * afterwards, so the correction has to be possible here.
 */
export default function InvoiceableOrderLines({
  lines,
  disabled,
  onChange,
}: Props) {
  const { t } = useTranslation();

  if (!lines.length) return null;

  const remaining = lines.filter((line) => !line.removed);

  return (
    <div className="mt-2 overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-700">
      <table className="w-full border-collapse text-sm max-md:block">
        <thead className="max-md:hidden">
          <tr className="border-b border-zinc-200 bg-zinc-50 text-left text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:border-zinc-700 dark:bg-zinc-800/60">
            <th className="px-3 py-2">{t("product", { defaultValue: "Product" })}</th>
            <th className="px-3 py-2 text-right">
              {t("quantity", { defaultValue: "Quantity" })}
            </th>
            <th className="px-3 py-2 text-right">{t("price", { defaultValue: "Price" })}</th>
            <th className="px-3 py-2 text-right">{t("total", { defaultValue: "Total" })}</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody className="max-md:block">
          {lines.map((line) => {
            const quantity = Number(line.quantity) || 0;
            const price = Number(line.price) || 0;
            const problem = lineProblem(line);
            const RemoveIcon = line.removed ? RotateCcw : Trash2;
            return (
              <tr
                key={line.externalCode}
                className={`border-b border-zinc-100 align-top last:border-0 max-md:grid max-md:grid-cols-2 max-md:gap-x-3 max-md:p-3 dark:border-zinc-800 ${
                  line.removed ? "opacity-40" : ""
                }`}
              >
                <td className="px-3 py-2 max-md:col-span-2 max-md:p-0 max-md:pb-2">
                  <span className={`text-sm ${line.removed ? "line-through" : ""}`}>
                    {line.title || "—"}
                  </span>
                  {line.description ? (
                    <div className="text-xs text-zinc-500">{line.description}</div>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-right max-md:p-0 max-md:text-left">
                  <span className={phoneLabelCls}>
                    {t("quantity", { defaultValue: "Quantity" })}
                  </span>
                  <input
                    type="number"
                    min={0}
                    step="any"
                    inputMode="decimal"
                    className={cellInputCls}
                    value={line.quantity}
                    disabled={disabled || line.removed}
                    aria-invalid={problem === "quantity"}
                    aria-label={t("quantity", { defaultValue: "Quantity" })}
                    onChange={(e) =>
                      onChange(line.externalCode, { quantity: e.target.value })
                    }
                  />
                  {problem === "quantity" ? (
                    <p className={errorTextCls}>
                      {t("invoice_line_quantity_required", {
                        defaultValue: "Above 0",
                      })}
                    </p>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-right max-md:p-0 max-md:text-left">
                  <span className={phoneLabelCls}>
                    {t("price", { defaultValue: "Price" })}
                  </span>
                  <input
                    type="number"
                    min={0}
                    step="any"
                    inputMode="decimal"
                    className={cellInputCls}
                    value={line.price}
                    disabled={disabled || line.removed}
                    aria-invalid={problem === "price"}
                    aria-label={t("price", { defaultValue: "Price" })}
                    onChange={(e) => onChange(line.externalCode, { price: e.target.value })}
                  />
                  {problem === "price" ? (
                    <p className={errorTextCls}>
                      {t("invoice_line_price_required", {
                        defaultValue: "0 or more",
                      })}
                    </p>
                  ) : null}
                </td>
                <td className="px-3 py-2 text-right font-medium tabular-nums max-md:flex max-md:items-center max-md:p-0 max-md:pt-2 max-md:text-left">
                  <Currency value={quantity * price} isSymbol />
                </td>
                <td className="px-3 py-2 text-right max-md:p-0 max-md:pt-2">
                  <button
                    type="button"
                    className={`${btnIconCls} max-md:size-11 ${
                      line.removed ? "" : "text-red-600 hover:text-red-700 dark:text-red-400"
                    }`}
                    // The last remaining line cannot go: an invoice with no
                    // lines is refused by the server anyway.
                    disabled={disabled || (!line.removed && remaining.length === 1)}
                    aria-label={
                      line.removed
                        ? t("restore", { defaultValue: "Restore" })
                        : t("remove", { defaultValue: "Remove" })
                    }
                    title={
                      line.removed
                        ? t("restore", { defaultValue: "Restore" })
                        : t("remove", { defaultValue: "Remove" })
                    }
                    onClick={() => onChange(line.externalCode, { removed: !line.removed })}
                  >
                    <RemoveIcon className="size-4" aria-hidden="true" />
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
